import React, { useEffect, useState, useRef } from "react";
import { motion } from "framer-motion";
import { X, Maximize, Minimize, Loader2, Volume2, VolumeX } from "lucide-react";
import useFullscreen from "@/hooks/useFullscreen";

const FlatImageViewer = ({ tour, tours = [], onClose, onSelectTour, soundOn = true, onToggleSound }) => {
  const containerRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const { isFullscreen, toggleFullscreen } = useFullscreen(containerRef);

  useEffect(() => setLoading(true), [tour.id]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && !document.fullscreenElement) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <motion.div
      ref={containerRef}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }}
      className="fixed inset-0 z-50 bg-black flex items-center justify-center"
      data-testid="image-viewer"
    >
      <img
        src={tour.video_url}
        alt={tour.title}
        className="max-h-full max-w-full object-contain"
        onLoad={() => setLoading(false)}
        data-testid="image-viewer-img"
      />

      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-[#04271B] z-20" data-testid="image-viewer-loading">
          <Loader2 className="w-8 h-8 text-[#D6B15E] animate-spin" strokeWidth={1.5} />
        </div>
      )}

      <div className="absolute top-0 left-0 right-0 z-30 flex items-center justify-between p-6">
        <div className="backdrop-blur-2xl bg-black/40 border border-white/10 px-5 py-3 rounded-full">
          <p className="text-[10px] tracking-[0.2em] uppercase text-[#D6B15E]">{tour.room}</p>
          <p className="font-heading text-lg leading-tight">{tour.title}</p>
        </div>
        <button
          onClick={onClose}
          data-testid="image-viewer-close-btn"
          className="w-12 h-12 flex items-center justify-center backdrop-blur-2xl bg-black/40 border border-white/10 rounded-full hover:bg-white/10 transition-colors"
          aria-label="Close"
        >
          <X className="w-5 h-5" strokeWidth={1.5} />
        </button>
      </div>

      <div className="absolute bottom-0 left-0 right-0 z-30 p-6 flex flex-col items-center gap-4">
        {tours.length > 1 && (
          <div className="flex gap-3 max-w-full overflow-x-auto pb-1">
            {tours.map((t) => (
              <button
                key={t.id}
                onClick={() => onSelectTour(t)}
                data-testid={`image-scene-${t.id}`}
                className={`shrink-0 px-4 py-2 rounded-full text-xs tracking-widest uppercase border transition-colors backdrop-blur-2xl ${
                  t.id === tour.id ? "bg-[#D6B15E] text-black border-[#D6B15E]" : "bg-black/40 text-white border-white/10 hover:bg-white/10"
                }`}
              >
                {t.room}
              </button>
            ))}
          </div>
        )}
        <div className="flex items-center gap-3">
          <button
            onClick={onToggleSound}
            data-testid="image-mute-toggle"
            title={soundOn ? "Mute ambient sound" : "Play ambient sound"}
            className="backdrop-blur-2xl bg-black/40 border border-white/10 rounded-full w-12 h-12 flex items-center justify-center hover:bg-white/10 transition-colors"
            aria-label={soundOn ? "Mute ambient sound" : "Play ambient sound"}
          >
            {soundOn ? <Volume2 className="w-5 h-5 text-[#D6B15E]" strokeWidth={1.5} /> : <VolumeX className="w-5 h-5" strokeWidth={1.5} />}
          </button>
          <button
            onClick={toggleFullscreen}
            data-testid="image-fullscreen-toggle"
            className="backdrop-blur-2xl bg-black/40 border border-white/10 rounded-full px-6 py-3 flex items-center gap-3 hover:bg-white/10 transition-colors text-sm tracking-widest uppercase"
          >
            {isFullscreen ? <Minimize className="w-4 h-4" strokeWidth={1.5} /> : <Maximize className="w-4 h-4" strokeWidth={1.5} />}
            Fullscreen
          </button>
        </div>
      </div>
    </motion.div>
  );
};

export default FlatImageViewer;
